import { Bot, CheckCircle2, ShieldCheck, Sparkles, UserCheck, XCircle } from "lucide-react";
import { useId } from "react";
import type { DraftedFixResult } from "../../api/model.js";
import type { FindingView } from "../../api/types.js";
import { Badge } from "../../components/Badge.js";
import { displayRuleId } from "../../app/rule-id.js";

/**
 * A model-drafted one-line fix, rendered only after something other than the
 * rule that raised the finding agreed with it.
 *
 * ## The one thing this component must never do
 *
 * It must never display a patch the server did not hand it, and it must never
 * go looking for one. On a rejected verdict the route returns `patch: null`
 * deliberately: the line failed the check that the cited credential material is
 * gone from it, so it may still *contain* the secret. The line is kept in the
 * artifact row for audit and is not returned. There is no path from this panel
 * to that row, and adding one would defeat the verification by walking around
 * it -- the check would still run, and its answer would stop mattering.
 *
 * The converse is why an accepted patch is safe to put on screen: acceptance is
 * the proof that the credential material is no longer in the line. The safety of
 * displaying it is not a judgement this component makes, it is the verdict's.
 *
 * ## Why the marker works the same way as the explanation panel's
 *
 * Same reasoning as `ModelExplanation.tsx`, same mechanism, and deliberately not
 * shared: the marker is a constant in this module emitted from props that carry
 * no way to switch it off. There is no `showMarker`, no `variant`, no
 * `hideLabel`. A payload cannot disable something the renderer never asks it
 * about, and prose cannot forge `data-model-generated="true"` because React
 * escapes text into text nodes.
 *
 * ## Why this validates a typed value again
 *
 * `DraftedFixResult` is what the API *promises*. This module treats it as what
 * arrived. `outcome` decides everything rendered here, so a response with an
 * outcome outside the four known ones renders nothing rather than falling
 * through to a default branch -- an unrecognised outcome is not a reason to
 * guess, least of all in the direction of showing a patch.
 *
 * ## What "accepted" does not mean
 *
 * Nothing is applied. No repository is written and no remediation proposal is
 * created; the decision is a record of an operator's judgement, and they copy
 * the line by hand. The panel says so where the button is, not in a footnote.
 */

/** The words a reader sees. A constant, so nothing that arrives can change it. */
export const MODEL_DRAFTED_FIX_MARKER = "Model-drafted fix";

/** The structural half of the marker, identical in kind to the explanation panel's. */
export const MODEL_DRAFTED_FIX_MARKER_ATTRIBUTE = "data-model-generated";

/**
 * A replacement line is one line. The server screens it for secret-like material
 * before returning it, and the verdict has already established the cited
 * credential is absent, so this is not a security limit -- it is a rendering
 * one: a "line" long enough to be a file is not something to paste into a code
 * block, and a control character in it has no business reaching the DOM.
 */
const MAX_REPLACEMENT_LINE_LENGTH = 2000;

function hasControlCharacter(text: string): boolean {
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    if (code === 0x7f || (code < 0x20 && code !== 0x09)) return true;
  }
  return false;
}

/**
 * The line to show, or `null`.
 *
 * Returns `null` for every outcome except `verified` -- including when a caller
 * hands over a response that carries both `outcome: "rejected"` and a non-null
 * `patch`. That combination should be impossible, which is exactly why it is
 * checked here: if it ever becomes possible, the answer is to show nothing.
 */
export function verifiedReplacementLine(result: DraftedFixResult): string | null {
  if (result.outcome !== "verified") return null;
  if (result.verdict !== null && !result.verdict.accepted) return null;
  const line = result.patch?.replacementLine;
  if (typeof line !== "string") return null;
  if (line.length === 0 || line.length > MAX_REPLACEMENT_LINE_LENGTH) return null;
  if (hasControlCharacter(line)) return null;
  return line;
}

/** A displayable advisory line. Only the verified variant is decision-eligible. */
export function displayReplacementLine(result: DraftedFixResult): string | null {
  if (result.outcome !== "verified" && result.outcome !== "drafted_unverified") return null;
  if (result.outcome === "verified" && result.verdict !== null && !result.verdict.accepted) {
    return null;
  }
  if (result.outcome === "drafted_unverified" && result.verdict?.accepted) return null;
  const line = result.patch?.replacementLine;
  if (typeof line !== "string") return null;
  if (line.length === 0 || line.length > MAX_REPLACEMENT_LINE_LENGTH) return null;
  if (hasControlCharacter(line)) return null;
  return line;
}

const OUTCOME_COPY: Readonly<
  Record<DraftedFixResult["outcome"], { readonly title: string; readonly tone: string }>
> = {
  verified: { title: "Drafted and independently checked", tone: "success" },
  drafted_unverified: { title: "Draft ready — verification required", tone: "warning" },
  rejected: { title: "Drafted and refused by the check", tone: "danger" },
  refused: { title: "No fix was drafted", tone: "neutral" },
  unavailable: { title: "The provider could not be reached", tone: "warning" },
};

export function ModelDraftedFixPanel({
  finding,
  result,
  decision,
  decisionPending = false,
  onDecide,
}: {
  finding?: FindingView | undefined;
  result?: DraftedFixResult | undefined;
  /** Set once the operator has ruled, so the controls retire rather than 409. */
  decision?: "accepted" | "rejected" | null | undefined;
  decisionPending?: boolean;
  onDecide?: ((artifactId: string, decision: "accepted" | "rejected") => void) | undefined;
}) {
  const headingId = useId();
  if (result === undefined) return null;
  const copy = OUTCOME_COPY[result.outcome];
  // An outcome outside the four known ones renders nothing. Not a default
  // branch: guessing here could only guess toward showing something.
  if (copy === undefined) return null;
  const line = displayReplacementLine(result);
  const securityValue =
    finding?.analysisFamily === "secrets"
      ? "Removes credential-like material from the cited source path, reducing accidental disclosure and preventing future builds from embedding the same value. Rotate any real credential separately; changing source does not invalidate an exposed secret."
      : finding?.ruleId === "ASSURE-JS-003"
        ? "Removes the runtime JavaScript compiler from the cited path so request-controlled text remains data rather than executable code. A property allowlist or purpose-built parser also makes the accepted input language explicit and auditable."
        : `Breaks the reported behavior${finding ? ` identified by ${displayRuleId(finding.ruleId)}` : ""} at the replayed source location while keeping the proposed change narrow enough for a human to review.`;
  const functionalImpact =
    finding?.analysisFamily === "secrets"
      ? "The application must receive the value through an approved runtime secret provider or environment boundary. Validate startup, missing-secret failure behavior, deployment injection, and rotation before release."
      : finding?.ruleId === "ASSURE-JS-003"
        ? "Dynamic expressions that previously executed as JavaScript may stop working. Test every supported template or expression, malformed input, escaping, missing fields, and output encoding; use a finite parser or operation allowlist if the application needs more than direct property interpolation."
        : "Re-run the focused test for this code path, the originating detector, and the surrounding integration tests. Confirm both the security property and the intended success path before applying it.";

  return (
    <section className="inspector-section inspector-section--model" aria-labelledby={headingId}>
      <div className="inspector-section__heading">
        <Bot aria-hidden="true" size={16} />
        <h3 data-model-generated="true" id={headingId}>
          {MODEL_DRAFTED_FIX_MARKER}
        </h3>
        <Badge label="Not part of the finding record" tone="warning" />
      </div>
      <div className={`model-fix__hero model-fix__hero--${result.outcome}`}>
        <span className="model-fix__hero-icon" aria-hidden="true">
          {result.outcome === "verified" || result.outcome === "drafted_unverified" ? (
            <Sparkles size={20} />
          ) : (
            <XCircle size={20} />
          )}
        </span>
        <div>
          <span>AI remediation workspace</span>
          <strong>{copy.title}</strong>
        </div>
      </div>

      <ol className="model-fix__stages" aria-label="Drafted fix assurance stages">
        <li className="model-fix__stage model-fix__stage--complete">
          <Sparkles aria-hidden="true" size={16} />
          <span>
            <strong>Generated</strong>
            <small>One-line candidate</small>
          </span>
        </li>
        <li
          className={
            result.outcome === "verified"
              ? "model-fix__stage model-fix__stage--complete"
              : "model-fix__stage"
          }
        >
          <ShieldCheck aria-hidden="true" size={16} />
          <span>
            <strong>Independent check</strong>
            <small>
              {result.outcome === "verified"
                ? "Corroborated"
                : result.outcome === "drafted_unverified"
                  ? "No independent checker"
                  : "Not cleared"}
            </small>
          </span>
        </li>
        <li
          className={decision ? "model-fix__stage model-fix__stage--complete" : "model-fix__stage"}
        >
          <UserCheck aria-hidden="true" size={16} />
          <span>
            <strong>Human decision</strong>
            <small>{decision ?? "Required"}</small>
          </span>
        </li>
      </ol>

      {result.reason === null ? null : <p className="muted-copy">{result.reason}</p>}

      {line === null ? null : (
        <>
          <div className="model-fix__verified-heading">
            {result.outcome === "verified" ? (
              <CheckCircle2 aria-hidden="true" size={18} />
            ) : (
              <ShieldCheck aria-hidden="true" size={18} />
            )}
            <div>
              <strong>
                {result.outcome === "verified"
                  ? "Verified replacement"
                  : "Unverified replacement proposal"}
              </strong>
              <span>
                {result.outcome === "verified"
                  ? "Safe to review; never auto-applied"
                  : "Human and test verification required; never auto-applied"}
              </span>
            </div>
          </div>
          <p className="model-fix__explanation">
            {result.outcome === "verified"
              ? "A signal independent of the rule that raised this finding corroborated the change. Nothing has been applied — review it before use."
              : "The originating detector went quiet, but Q Squared has no independent static checker for this behavior. Treat this as an engineering proposal, inspect the diff, and prove behavioral equivalence with focused tests before use."}
          </p>
          <pre className="model-drafted-fix__line">
            <code>{line}</code>
          </pre>
          <div className="model-fix__impact-grid">
            <article>
              <h4>Why this fix is worth reviewing</h4>
              <p>{securityValue}</p>
            </article>
            <article>
              <h4>Functional impact and verification</h4>
              <p>{functionalImpact}</p>
            </article>
          </div>
        </>
      )}

      {result.outcome === "rejected" ? (
        <p className="muted-copy">
          The drafted line is withheld. It failed the check that the cited credential is no longer
          present, so it may still contain it.
        </p>
      ) : null}

      {result.verdict === null ? null : (
        <ul className="model-drafted-fix__signals">
          {result.verdict.signals.map((signal) => (
            <li key={signal.signalId}>
              <span className="model-drafted-fix__signal-id">{signal.signalId}</span>
              <span className="model-drafted-fix__signal-outcome">{signal.outcome}</span>
            </li>
          ))}
          {result.verdict.originatingRuleSilenced ? (
            <li>
              <span className="model-drafted-fix__signal-id">originating rule</span>
              <span className="model-drafted-fix__signal-outcome">
                went quiet — on its own this never means accepted
              </span>
            </li>
          ) : null}
        </ul>
      )}

      {/* Controls appear only where there is something to rule on: a verified
          patch with an artifact row. A refusal and an unavailable provider
          produced no proposal, so there is no judgement to record. */}
      {onDecide && result.outcome === "verified" && result.artifactId !== null && line !== null ? (
        decision ? (
          <p className="muted-copy" aria-live="polite">
            Recorded as {decision}. A decision is final and cannot be changed.
          </p>
        ) : (
          <div className="model-drafted-fix__decision">
            <button
              className="button"
              type="button"
              disabled={decisionPending}
              onClick={() => onDecide(result.artifactId as string, "accepted")}
            >
              Accept this draft
            </button>
            <button
              className="button button--ghost"
              type="button"
              disabled={decisionPending}
              onClick={() => onDecide(result.artifactId as string, "rejected")}
            >
              Reject
            </button>
            <p className="muted-copy">
              Recording a decision changes nothing in your repository. It is attributable, and it
              cannot be changed afterwards.
            </p>
          </div>
        )
      ) : null}
    </section>
  );
}
